import { SyntheticEvent, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { useConditionalEffect, usePrevious } from 'react-delta';
import { useMutation, useQuery } from '@apollo/client';
import { Form } from 'react-bootstrap';
import { PropsValue } from 'react-select';

import ELEMENT from 'common/element';
import log from 'common/ScalyrLogger';
import { DASHBOARDS } from 'components/userAccounts/queries';
import { EDIT_GROUP_PERMISSIONS, GROUPS_LIST } from 'components/manageGroups/queries';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { ACCESS_OPTIONS, ACCESS_OPTIONS_MAP } from 'components/userAccounts/consts';
import { USER_ACCESS } from 'components/userAccounts/types';
import { AccessType, Dashboard } from 'common/graphqlTypes';
import { Group } from 'components/manageGroups/ManageGroupsPage';
import Input from 'components/common/Input/Input';
import Select, { OptionType } from 'components/common/ReactSelect/Select';
import Dialog from 'components/common/Dialog/Dialog';

import './EditGroupDialog.scss';

type EditGroupDialogProps = {
  show: boolean;
  group: Group | null;
  close: () => void;
};

/**
 * Dialog for managing Azure/ Okta SCIM group permissions, allowed searches, and dashboards.
 * @prop show   boolean to toggle dialog on and off.
 * @prop group  group object containing data regarding SCIM group permission
 * @prop close  callback to execute on close.
 */
const EditGroupDialog = ({ show, group, close }: EditGroupDialogProps) => {
  const [permission, setPermission] = useState(group?.permission || USER_ACCESS.LIMITED);
  const [allowedSearch, setAllowedSearch] = useState(group?.allowedSearch || '');
  const [allowedDashboards, setAllowedDashboards] = useState(group?.allowedDashboards || []);
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const { loading: dashboardLoading, data: dashboardsData } = useQuery(DASHBOARDS);
  const [editGroup] = useMutation(EDIT_GROUP_PERMISSIONS, {
    onCompleted: () => {
      setPageAlertConfig({ message: 'Group Permission Updated', variant: PageAlertVariant.SUCCESS, autoHide: true });
    },
    refetchQueries: [
      {
        query: GROUPS_LIST,
      },
    ],
    onError: (error) => {
      log.debug('Error editing SCIM group', error);
      setPageAlertConfig({
        message: 'Something went wrong. Group not updated, Please try again',
        variant: PageAlertVariant.ERROR,
        autoHide: true,
      });
    },
  });

  const prevGroup = usePrevious(group);
  useConditionalEffect(() => {
    setPermission(group?.permission || USER_ACCESS.LIMITED);
    setAllowedSearch(group?.allowedSearch || '');
    setAllowedDashboards(group?.allowedDashboards || []);
  }, group !== prevGroup);

  const onAllowedSearchChange = (event: SyntheticEvent): void => {
    const inputTarget = event.target as HTMLInputElement;
    setAllowedSearch(inputTarget.value);
  };

  const setDashboards = (values: PropsValue<OptionType>): void => {
    const dashboards: string[] = Array.isArray(values)
      ? values.map(({ value }: OptionType): string => value as string)
      : [];
    setAllowedDashboards(dashboards);
  };

  const handleOk = (): void => {
    editGroup({
      variables: {
        groupId: group?.id,
        permission,
        allowedSearch,
        allowedDashboards,
      },
    });
  };

  return (
    <Dialog
      className="EditGroupDialog"
      title="Edit Permissions"
      show={show}
      close={close}
      okCallback={handleOk}
      onEnterCallback={handleOk}
    >
      <div className="EditGroupDialog__Body">
        <div className="EditGroupDialog__GroupName">{group?.displayName}</div>
        <Form className="EditGroupDialog__Form">
          <div>
            <Form.Label>Permission Level</Form.Label>
            <Select
              onChange={(selectedOption) => {
                const { value } = selectedOption as OptionType;
                setPermission(value as AccessType);
              }}
              value={{ value: permission, label: ACCESS_OPTIONS_MAP[permission] }}
              defaultValue={{ value: USER_ACCESS.LIMITED, label: ACCESS_OPTIONS_MAP[USER_ACCESS.LIMITED] }}
              options={ACCESS_OPTIONS.map(
                (name: string): OptionType => ({ value: name, label: ACCESS_OPTIONS_MAP[name] }),
              )}
              idContainer={ELEMENT.manageGroupsAccessSelect}
            />
          </div>
          <div>
            <Form.Label>Allowed Search</Form.Label>
            <Input
              onChange={onAllowedSearchChange}
              value={allowedSearch}
              placeholder="Enter allowed searches"
              testId={ELEMENT.manageGroupsAllowedSearchInput}
            />
          </div>
          <div>
            <Form.Label>Allowed Dashboards</Form.Label>
            <Select
              value={allowedDashboards.map((name) => ({ value: name, label: name }))}
              isMulti
              onChange={setDashboards}
              isLoading={dashboardLoading}
              options={dashboardsData.dashboardsV2.map(
                (dashboard: Dashboard): OptionType => ({ value: dashboard.name, label: dashboard.name }),
              )}
              idContainer={ELEMENT.userAccountsDashboardsSelect}
            />
          </div>
        </Form>
      </div>
    </Dialog>
  );
};

export default EditGroupDialog;
